/**
 * „Auszahlung & Vorschuss" (Phase I, Kapitel 16) — eigene Ansicht auf der
 * Übersichtsseite. Zeigt, ob die Auszahlung für den aktuell angezeigten
 * Abrechnungszeitraum schon bestätigt wurde, bietet „Vorschuss melden" immer
 * an, und listet eigene offene/entschiedene Vorschüsse.
 */

import { useState } from 'react';
import { usePayouts } from '../data/payouts';
import { PayoutModal } from './PayoutModal';
import { formatMoney, formatDateTime } from '../lib/format';
import type { BillingCycle } from '../lib/payroll';

interface Props {
  cycle: BillingCycle;
  suggestedAmount: number;
}

export function PayoutCard({ cycle, suggestedAmount }: Props) {
  const { data: payouts } = usePayouts();
  const [modal, setModal] = useState<'regular' | 'advance' | null>(null);

  const regularForCycle = (payouts ?? []).find(
    (p) => p.type === 'regular' && p.periodFrom === cycle.from && p.periodTo === cycle.to,
  );
  const advances = (payouts ?? []).filter((p) => p.type === 'advance');

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-sm uppercase tracking-wider text-slate-400">Auszahlung & Vorschuss</h3>

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm">Auszahlung für {cycle.label}</p>
          {regularForCycle ? (
            <p className="text-xs text-emerald-400">
              Bestätigt am {formatDateTime(regularForCycle.createdAt)} ·{' '}
              {formatMoney(regularForCycle.amount)}
            </p>
          ) : (
            <p className="text-xs text-slate-500">Noch nicht bestätigt.</p>
          )}
        </div>
        {!regularForCycle && (
          <button
            onClick={() => setModal('regular')}
            className="text-xs bg-brand hover:bg-brand-dark rounded px-3 py-1.5 shrink-0"
          >
            Erhalt bestätigen
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/60">
        <p className="text-sm text-slate-400">Vorschuss erhalten?</p>
        <button
          onClick={() => setModal('advance')}
          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5 shrink-0"
        >
          Vorschuss melden
        </button>
      </div>

      {advances.length > 0 && (
        <ul className="space-y-1.5 pt-1">
          {advances.map((a) => (
            <li key={a.id} className="text-xs flex items-center justify-between gap-2">
              <span className="text-slate-400">
                Vorschuss {formatMoney(a.amount)} · {formatDateTime(a.createdAt)}
              </span>
              <span
                className={
                  a.status === 'reported'
                    ? 'text-amber-400'
                    : a.status === 'confirmed'
                      ? 'text-emerald-400'
                      : 'text-red-400'
                }
              >
                {a.status === 'reported'
                  ? 'wartet auf Chef'
                  : a.status === 'confirmed'
                    ? 'bestätigt'
                    : 'abgelehnt'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {modal && (
        <PayoutModal
          mode={modal}
          period={modal === 'regular' ? cycle : undefined}
          defaultAmount={modal === 'regular' ? suggestedAmount : undefined}
          onClose={() => setModal(null)}
          onSaved={() => setModal(null)}
        />
      )}
    </section>
  );
}
